import { useEffect, useState, ReactElement, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Checkbox, Chip, FormControlLabel, FormHelperText, Stack, useTheme } from '@mui/material';
import { Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import {
  Annexe,
  ConstructionClass,
  DeflectionLimitRoof,
  DeflectionLimitWall,
  IOption,
  LoadFactor,
  ParamAndCoefficient,
  Reference,
  Standard,
  Standards,
  SupportWidth,
  UserStandard,
} from '../../types/domainTypes';
import { EditDialog } from 'components/EditDialog/EditDialog';
import { DeflectionLimitsDialog, HasCustomisedValues } from 'pages/ProjectDashboard/DeflectionLimitsDialog';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import { AppBaseButton } from 'components/AppBaseButton/AppBaseButton';
import { EditOutlined } from '@mui/icons-material';
import { ParametersAndCoefficientsDialog } from './ParametersAndCoefficientsDialog';
import { LoadFactorsDialog } from './LoadFactorsDialog';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { useSnackbar } from 'notistack';
import { useSaveUserStandardTemplateMutation } from 'redux/services/projects';
import { useAppSelector } from 'redux/hooks';

interface StandardDialogProps {
  data?: Standard;
  existingUserStandard?: UserStandard | undefined;
  options: Standards[];
  supportWidthOptions?: SupportWidth[];
  onClose: () => void;
  onSave: (standard: Standard) => void;
  compareStandards?: (projectStandard: Standard | undefined, userStandardTemplate: UserStandard | undefined) => boolean;
  title: string;
  displayCalculationWarning?: boolean;
  displayModifiedStandardsWarning?: boolean;
}

type StandardOptionProps = {
  label: string;
  onChange: (value: IOption | null) => void;
  options: IOption[];
  value: IOption | null;
  error?: boolean;
  required?: boolean;
};

function SelectedOptionText({ label, value }: { label: string; value?: string }) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1}>
      <Typography>&ensp;</Typography>
      <Typography style={{ color: theme.palette.text.secondary }}>{label}:</Typography>
      <Typography fontFamily="KingspanBrownTT-Bold">{value}</Typography>
    </Stack>
  );
}

function MultipleOptions({ label, onChange, options, value, required, error }: StandardOptionProps): ReactElement {
  const { t } = useTranslation();
  useEffect(() => {
    if (value !== null && options.indexOf(value) === -1) {
      onChange(null);
    }
  }, [onChange, options, value]);

  return (
    <FormControl fullWidth error={error}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        onChange={(e) => {
          onChange(e.target.value as IOption);
        }}
        value={value ? value : ''}
      >
        {options.map((option, index) => (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <MenuItem key={index} value={option as any}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
      {required && !error && <FormHelperText>{`*${t('common.required-helper-text')}`}</FormHelperText>}
      {error && <FormHelperText>{t('common.required-error-text')}</FormHelperText>}
    </FormControl>
  );
}

function NullOption({ onChange, value }: StandardOptionProps): ReactElement {
  useEffect(() => {
    if (value !== null) {
      onChange(null);
    }
  }, [onChange, value]);
  return <></>;
}

function SingleOption({ label, onChange, options, value }: StandardOptionProps): ReactElement | null {
  useEffect(() => {
    if (value !== options[0]) {
      onChange(options[0]);
    }
  }, [onChange, options, value]);

  const displayValue = options[0].name;
  if (!displayValue) {
    return null;
  }

  return <SelectedOptionText label={label} value={displayValue} />;
}

function StandardOption({ label, onChange, options, value, error }: StandardOptionProps): ReactElement | null {
  if (!options || options.length === 0) {
    return <NullOption label={label} options={options} onChange={onChange} value={value} />;
  } else if (options.length === 1) {
    return <SingleOption label={label} options={options} onChange={onChange} value={value} />;
  } else {
    return <MultipleOptions label={label} options={options} onChange={onChange} value={value} required error={error} />;
  }
}

function getOptions(option: Standards | Reference | Annexe | ConstructionClass | ParamAndCoefficient | IOption | null): IOption[] {
  if (option) {
    if ('references' in option) {
      return option.references;
    } else if ('annexes' in option) {
      return option.annexes;
    } else if ('constructionClasses' in option) {
      return option.constructionClasses;
    } else if ('paramsAndCoefficients' in option) {
      return option.paramsAndCoefficients;
    } else if ('loadFactors' in option) {
      return option.loadFactors;
    }
  }

  return [];
}

function empty<T>(): T | null {
  return null;
}

const convertSupportWidthToString = (supportWidthOption: SupportWidth) => {
  return `${supportWidthOption.end}/${supportWidthOption.intermediate}`;
};

function getExistingStandardValues({ data, options }: { data: Standard | UserStandard | undefined; options: Standards[] }) {
  if (data === undefined || options === undefined) return undefined;
  const supportWidth = data.supportWidth ? convertSupportWidthToString(data.supportWidth) : '';
  const country = options.find((c) => c.name === data?.country);
  const references = country?.references.find((ref) => ref.name === data?.reference);
  const annexes = references?.annexes.find((annexe) => annexe.name === data?.annexe);
  const constructionClasses = annexes?.constructionClasses.find((cc) => cc.name === data?.constructionClass);
  const paramsAndCoefficients = constructionClasses?.paramsAndCoefficients.find((pc) => pc.name === data?.paramsAndCoefficients);
  const loadFactors = paramsAndCoefficients?.loadFactors.find((loadFactor) => loadFactor.name === data?.loadFactors);
  const deflectionLimitRoof = data?.deflectionLimitRoof;
  const deflectionLimitWall = data?.deflectionLimitWall;
  return {
    supportWidth,
    country,
    references,
    annexes,
    constructionClasses,
    paramsAndCoefficients,
    loadFactors,
    deflectionLimitRoof,
    deflectionLimitWall,
  };
}

function DeflectionLimit({
  labelText,
  buttonText,
  deflectionLimit,
  standard,
  onSave,
}: {
  labelText: string;
  buttonText: string;
  deflectionLimit: DeflectionLimitRoof | DeflectionLimitWall;
  standard: Standards;
  onSave: (deflectionLimitData: DeflectionLimitRoof | DeflectionLimitWall) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleSave = (data: DeflectionLimitRoof | DeflectionLimitWall) => {
    setIsDialogOpen(false);
    onSave(data);
  };
  const { customized, ...deflectionLimitWithoutCustomized } = deflectionLimit;
  const isCustomised = HasCustomisedValues(standard, deflectionLimitWithoutCustomized);

  return (
    <>
      <Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <SelectedOptionText label={labelText} value={deflectionLimit.name} />
          {isCustomised && (
            <Chip
              data-testid="customised-deflection-limit"
              sx={{ backgroundColor: '#e0e8f1', px: '5px' }}
              label={
                <Typography variant="caption" fontSize={12}>
                  {t('common.label-customised')}
                </Typography>
              }
            />
          )}
        </Stack>
        <ShrinkToIconButton hideTextWhenXS={false} onClick={() => setIsDialogOpen(true)} icon={<EditOutlined />} buttonText={buttonText} />
      </Stack>
      {isDialogOpen && (
        <DeflectionLimitsDialog data={deflectionLimit} standard={standard} onClose={() => setIsDialogOpen(false)} onSave={handleSave} />
      )}
    </>
  );
}

export function StandardDialog({
  data,
  existingUserStandard,
  options,
  supportWidthOptions,
  onClose,
  onSave,
  compareStandards,
  title,
  displayCalculationWarning,
  displayModifiedStandardsWarning,
}: StandardDialogProps): ReactElement {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isCalculationWarningOpen, setIsCalculationWarningOpen] = useState(true);
  const [isModifiedStandardsWarningOpen, setIsModifiedStandardsWarningOpen] = useState(true);
  const [isSaveStandardTemplateChecked, setIsSaveStandardTemplateChecked] = useState(false);
  const [isSaveStandardTemplateConfirmationOpen, setIsSaveStandardTemplateConfirmationOpen] = useState(false);
  const userEmail = useAppSelector((state) => state.user.email);

  const values = getExistingStandardValues({ data, options });
  const isFormInEdit = values !== undefined;

  const supportWidthOptionsStrings: string[] = [];

  if (supportWidthOptions) {
    supportWidthOptions.forEach((supportWidthOption) => supportWidthOptionsStrings.push(convertSupportWidthToString(supportWidthOption)));
  }

  interface SelectionState {
    supportWidth: string;
    country: Standards | null;
    references: IOption | null;
    annexes: IOption | null;
    constructionClasses: IOption | null;
    paramsAndCoefficients: ParamAndCoefficient | null;
    loadFactors: LoadFactor | null;
    deflectionLimitRoof: DeflectionLimitRoof | null;
    deflectionLimitWall: DeflectionLimitWall | null;
  }

  const [selection, setSelection] = useState<SelectionState>({
    supportWidth: values?.supportWidth || '',
    country: values?.country || empty<Standards>(),
    references: values?.references || empty<IOption>(),
    annexes: values?.annexes || empty<IOption>(),
    constructionClasses: values?.constructionClasses || empty<IOption>(),
    paramsAndCoefficients: values?.paramsAndCoefficients || empty<ParamAndCoefficient>(),
    loadFactors: values?.loadFactors || empty<LoadFactor>(),
    deflectionLimitRoof: values?.deflectionLimitRoof || empty<DeflectionLimitRoof>(),
    deflectionLimitWall: values?.deflectionLimitWall || empty<DeflectionLimitWall>(),
  });

  const [IsParametersAndCoefficientsDialogOpen, setIsParametersAndCoefficientsDialogOpen] = useState(false);
  const [IsLoadFactorsDialogOpen, setIsLoadFactorsDialogOpen] = useState(false);

  const [saveUserStandardTemplate, saveUserStandardTemplateResult] = useSaveUserStandardTemplateMutation();
  useEffect(() => {
    if (saveUserStandardTemplateResult.error) {
      enqueueSnackbar(t('project-details-page.snackbar-save-standard-template-error'), {
        variant: 'error',
      });
    } else if (saveUserStandardTemplateResult.isSuccess) {
      enqueueSnackbar(t('project-details-page.snackbar-save-standard-template-success'), {
        variant: 'success',
      });
    }
    setIsSaveStandardTemplateConfirmationOpen(false);
  }, [enqueueSnackbar, saveUserStandardTemplateResult, setIsSaveStandardTemplateConfirmationOpen, t]);

  function updateSelection(property: Partial<SelectionState>) {
    setIsSubmit(false);
    setSelection({ ...selection, ...property });
    setIsDirty(true);
  }

  const saveDeflectionLimit = (deflectionLimitData: DeflectionLimitWall | DeflectionLimitRoof) => {
    const isRoofDialog = 'pressureLoadWithCreep' in deflectionLimitData && 'cantileverLoadWithCreep' in deflectionLimitData;

    if (isRoofDialog) {
      updateSelection({ deflectionLimitRoof: deflectionLimitData });
    } else {
      updateSelection({ deflectionLimitWall: deflectionLimitData });
    }
  };

  const getStandardFromSelections = (): Standard => {
    const supportValues = selection.supportWidth.split('/');
    const supportWidth: SupportWidth = {
      end: parseInt(supportValues[0]),
      intermediate: parseInt(supportValues[1]),
    };

    return {
      id: selection.loadFactors?.id,
      supportWidth,
      country: selection.country?.name,
      reference: selection.references?.name,
      annexe: selection.annexes?.name,
      constructionClass: selection.constructionClasses?.name,
      paramsAndCoefficients: selection.paramsAndCoefficients?.name,
      loadFactors: selection.loadFactors?.name,
      deflectionLimitRoof: selection.deflectionLimitRoof || undefined,
      deflectionLimitWall: selection.deflectionLimitWall || undefined,
    };
  };

  const saveStandard = (shouldSaveTemplate: boolean) => {
    setIsSubmit(true);
    let field: keyof typeof selection;
    for (field in selection) {
      if (selection[field] === null) return;
    }

    const standard: Standard = getStandardFromSelections();
    onSave(standard);

    if (shouldSaveTemplate) {
      const userStandard: UserStandard = { ...standard, user: userEmail };
      saveUserStandardTemplate({ userStandard });
    }
  };

  const handleSubmit = () => {
    if (isSaveStandardTemplateChecked) {
      setIsSaveStandardTemplateConfirmationOpen(true);
    } else {
      // save Standard as usual without saving as a user template
      saveStandard(false);
    }
  };

  const resetStandardTemplate = () => {
    const userStandardValues = getExistingStandardValues({ data: existingUserStandard, options });
    setIsSaveStandardTemplateChecked(false);
    updateSelection({
      supportWidth: userStandardValues?.supportWidth || '',
      country: userStandardValues?.country || empty<Standards>(),
      references: userStandardValues?.references || empty<IOption>(),
      annexes: userStandardValues?.annexes || empty<IOption>(),
      constructionClasses: userStandardValues?.constructionClasses || empty<IOption>(),
      paramsAndCoefficients: userStandardValues?.paramsAndCoefficients || empty<ParamAndCoefficient>(),
      loadFactors: userStandardValues?.loadFactors || empty<LoadFactor>(),
      deflectionLimitRoof: userStandardValues?.deflectionLimitRoof || empty<DeflectionLimitRoof>(),
      deflectionLimitWall: userStandardValues?.deflectionLimitWall || empty<DeflectionLimitWall>(),
    });
  };

  const shouldDisableButton = () => {
    if (compareStandards) {
      if (existingUserStandard && !compareStandards(getStandardFromSelections(), existingUserStandard)) return false;
      return compareStandards(getStandardFromSelections(), existingUserStandard);
    } else {
      return !isDirty;
    }
  };

  const shouldDisableSubmit = () => {
    if (selection.country?.name === 'France') {
      return !selection.paramsAndCoefficients || !selection.loadFactors;
    } else if (selection.country?.name === 'The Netherlands') {
      return !selection.constructionClasses;
    } else {
      return !selection.deflectionLimitRoof;
    }
  };

  const customButtons: JSX.Element = (
    <>
      {existingUserStandard && (
        <Box
          sx={{
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'flex-start',
          }}
        >
          <AppBaseButton
            variant="contained"
            disabled={shouldDisableButton()}
            onClick={() => resetStandardTemplate()}
            data-testid="standards-reset-btn"
          >
            {t('project-details-page.standard-dialog.label-reset-standard-template')}
          </AppBaseButton>
        </Box>
      )}
      <AppBaseButton
        variant="contained"
        disabled={shouldDisableSubmit()}
        onClick={() => handleSubmit()}
        data-testid="standards-submit-btn"
        id="standards-submit-btn"
      >
        {isFormInEdit ? t('common.edit-dialog.action-save') : t('project-details-page.standard-dialog.action-add')}
      </AppBaseButton>
    </>
  );

  return (
    <>
      <EditDialog
        title={title}
        isDirty={isDirty}
        onClose={onClose}
        handleFormSubmit={handleSubmit}
        defaultErrorMessage={t('projects-page.project-dialog.error-message-save')}
        saveActionText={isFormInEdit ? t('common.edit-dialog.action-save') : t('project-details-page.standard-dialog.action-add')}
        customButtons={customButtons}
      >
        <Stack spacing={3} data-testid="parent-stack">
          {isFormInEdit && displayCalculationWarning && isCalculationWarningOpen && (
            <Alert severity="warning" onClose={() => setIsCalculationWarningOpen(false)}>
              <AlertTitle>
                <Typography fontFamily="KingspanBrownTT-Bold">
                  {t('project-details-page.standard-dialog.calculations-impacted-warning-title')}
                </Typography>
              </AlertTitle>
              {t('project-details-page.standard-dialog.calculations-impacted-warning-caption')}
            </Alert>
          )}
          {isFormInEdit && displayModifiedStandardsWarning && isModifiedStandardsWarningOpen && (
            <Alert severity="warning" onClose={() => setIsModifiedStandardsWarningOpen(false)}>
              {t('project-details-page.standard-dialog.calculations-modified-standard-warning')}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel id="project-support-width">{t('project-details-page.standard-dialog.label-support-widths')}</InputLabel>
            <Select
              labelId="project-support-width"
              data-testid="support-width-dropdown"
              id="project-support-width-select"
              value={selection.supportWidth}
              label={t('project-details-page.standard-dialog.label-support-widths')}
              onChange={(event) => {
                updateSelection({
                  supportWidth: event.target.value,
                });
              }}
            >
              {supportWidthOptionsStrings.map((sw: string) => (
                <MenuItem key={sw} value={sw}>
                  {sw}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{`*${t('common.required-helper-text')}`}</FormHelperText>
          </FormControl>

          {selection.supportWidth !== '' && (
            <StandardOption
              label={t('project-details-page.standard-dialog.label-country-field')}
              value={selection.country}
              options={options}
              error={isSubmit ? selection.country === null : undefined}
              onChange={(value) => {
                const valueStandards = value as Standards;
                updateSelection({
                  country: valueStandards,
                  deflectionLimitWall: valueStandards.deflectionLimitWall,
                  deflectionLimitRoof: valueStandards.deflectionLimitRoof,
                });
              }}
            />
          )}
          <StandardOption
            label={t('project-details-page.standard-dialog.label-references-field')}
            value={selection.references}
            options={getOptions(selection.country)}
            error={isSubmit ? selection.references === null : undefined}
            onChange={(value) => updateSelection({ references: value })}
          />
          <StandardOption
            label={t('project-details-page.standard-dialog.label-annexes-field')}
            value={selection.annexes}
            options={getOptions(selection.references)}
            error={isSubmit ? selection.annexes === null : undefined}
            onChange={(value) => updateSelection({ annexes: value })}
          />
          <StandardOption
            label={t('project-details-page.standard-dialog.label-construction-class-field')}
            value={selection.constructionClasses}
            options={getOptions(selection.annexes)}
            error={isSubmit ? selection.constructionClasses === null : undefined}
            onChange={(value) => updateSelection({ constructionClasses: value })}
          />
          <Stack>
            <StandardOption
              label={t('project-details-page.standard-dialog.label-params-coefficients-field')}
              value={selection.paramsAndCoefficients}
              options={getOptions(selection.constructionClasses)}
              error={isSubmit ? selection.paramsAndCoefficients === null : undefined}
              onChange={(value) => updateSelection({ paramsAndCoefficients: value as ParamAndCoefficient })}
            />
            {selection.paramsAndCoefficients && (
              <AppBaseButton onClick={() => setIsParametersAndCoefficientsDialogOpen(true)}>
                {t('project-details-page.standard-dialog.action-view-edit-params-and-coefficients')}
              </AppBaseButton>
            )}
            {IsParametersAndCoefficientsDialogOpen && selection.paramsAndCoefficients && (
              <ParametersAndCoefficientsDialog
                data={selection.paramsAndCoefficients}
                onClose={() => setIsParametersAndCoefficientsDialogOpen(false)}
              />
            )}
          </Stack>
          <Stack>
            <StandardOption
              label={t('project-details-page.standard-dialog.label-load-factors-field')}
              value={selection.loadFactors}
              options={getOptions(selection.paramsAndCoefficients)}
              error={isSubmit ? selection.loadFactors === null : undefined}
              onChange={(value) => updateSelection({ loadFactors: value as LoadFactor })}
            />
            {selection.loadFactors && (
              <AppBaseButton onClick={() => setIsLoadFactorsDialogOpen(true)}>
                {t('project-details-page.standard-dialog.action-view-edit-load-factors')}
              </AppBaseButton>
            )}
            {IsLoadFactorsDialogOpen && selection.loadFactors && (
              <LoadFactorsDialog data={selection.loadFactors} onClose={() => setIsLoadFactorsDialogOpen(false)} />
            )}
          </Stack>

          {selection.country && selection.deflectionLimitRoof && selection.deflectionLimitWall && selection.loadFactors && (
            <>
              <DeflectionLimit
                labelText={t('project-details-page.standard-dialog.label-deflection-limit-roof')}
                buttonText={t('project-details-page.standard-dialog.action-view-edit-dl-roof')}
                onSave={saveDeflectionLimit}
                standard={selection.country}
                deflectionLimit={selection.deflectionLimitRoof}
              />

              <DeflectionLimit
                labelText={t('project-details-page.standard-dialog.label-deflection-limit-wall')}
                buttonText={t('project-details-page.standard-dialog.action-view-edit-dl-wall')}
                onSave={saveDeflectionLimit}
                standard={selection.country}
                deflectionLimit={selection.deflectionLimitWall}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSaveStandardTemplateChecked}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setIsSaveStandardTemplateChecked(event.target.checked)}
                    name="save-standard"
                    data-testid="save-standard-checkbox"
                    sx={{ ml: 1, mb: 0.5 }}
                    disabled={shouldDisableButton()}
                  />
                }
                label={t('project-details-page.standard-dialog.label-save-default-standard')}
              />
            </>
          )}
        </Stack>
      </EditDialog>
      {isSaveStandardTemplateConfirmationOpen && (
        <ConfirmationDialog
          isOpen={isSaveStandardTemplateConfirmationOpen}
          isFullscreen={false}
          handleClose={() => setIsSaveStandardTemplateConfirmationOpen(false)}
          handleConfirm={() => saveStandard(true)}
          title={t('project-details-page.standard-dialog.save-standard-confirmation-title')}
          questionText={t('project-details-page.standard-dialog.save-standard-confirmation-body')}
          cancelText={t('project-details-page.standard-dialog.save-standard-confirmation-cancel-text')}
          confirmText={t('project-details-page.standard-dialog.save-standard-confirmation-confirm-text')}
        />
      )}
    </>
  );
}
